// src/App.jsx
import React, { useEffect, useState } from "react";
import { HashRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";

import Navbar from "./components/Navbar.jsx";
import BrandBar from "./components/BrandBar.jsx";
import Footer from "./components/Footer.jsx";
import ScrollToTop from "./components/ScrollToTop";

import Canonical from "./components/Canonical.jsx"
import Home from "./pages/Home.jsx";
import News from "./pages/News.jsx";
import Guides from "./pages/Guides.jsx";
import GuidePreview from "./pages/GuidePreview";
import Article from "./pages/Article.jsx";
import Enquire from "./pages/Enquire.jsx";
import PropertyTypes from "./pages/PropertyTypes";
import Locations from "./pages/Locations";
import Resources from "./pages/Resources.jsx";
import StampDutyCalc from "./pages/StampDutyCalc.jsx";
import RetirementCalc from "./pages/RetirementCalc.jsx";
import MortgageCalc from "./pages/MortgageCalc.jsx";
import About from "./pages/About.jsx";
import Privacy from "./pages/Privacy.jsx";
import Landing from "./pages/Landing.jsx";
import SeminarLanding from "./pages/SeminarLanding.jsx";

/** One-time consent grant (persisted). */
function ConsentGrant() {
  useEffect(() => {
    const key = "ga_consent_granted";
    if (localStorage.getItem(key)) return;
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
      });
      localStorage.setItem(key, "1");
    }
  }, []);
  return null;
}

/** GA4 + Meta page view on every route change (HashRouter-aware). */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    const page_location = window.location.href;
    const page_path = location.pathname + location.search + (location.hash || "");
    const page_title = document?.title || "Ethira";

    // GA4
    window.gtag?.("event", "page_view", { page_location, page_path, page_title });
    // Meta Pixel
    window.fbq?.("track", "PageView");
  }, [location]);
  return null;
}

// Little wrappers to adapt your existing components that expect props
function GuidePreviewWrapper() {
  const { slug } = useParams();
  return <GuidePreview slug={slug} />;
}
function ArticleWrapper() {
  const { slug } = useParams();
  return <Article slug={slug} />;
}
function NotFound() {
  return <div className="p-10 text-red-600 font-bold">Page not found</div>;
}

// Detect /landing to hide full nav but show brand strip
function useHideNavOnLanding() {
  const [hide, setHide] = useState(false);
  useEffect(() => {
    const check = () => {
      const hash = window.location.hash || "";
      const path = window.location.pathname || "";
      const isLanding =
        /^#\/landing(?:$|[/?#])/i.test(hash) || /^\/landing(?:$|[/?#])/i.test(path);
      setHide(isLanding);
    };
    check();
    window.addEventListener("hashchange", check);
    window.addEventListener("popstate", check);
    return () => {
      window.removeEventListener("hashchange", check);
      window.removeEventListener("popstate", check);
    };
  }, []);
  return hide;
}

export default function App() {
  const hideNav = useHideNavOnLanding();

  return (
    <div className="bg-white text-[#003366]">
      <Router>
        {/* put these FIRST inside Router */}
    
        <ConsentGrant />
        <PageViewTracker />
        <Canonical />
        <ScrollToTop />

        {hideNav ? <BrandBar /> : <Navbar />}

        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<Home />} />
          <Route path="/news" element={<News />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guide/:slug" element={<GuidePreviewWrapper />} />
          <Route path="/article/:slug" element={<ArticleWrapper />} />
          <Route path="/types" element={<PropertyTypes />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/enquire" element={<Enquire />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/stamp-duty" element={<StampDutyCalc />} />
          <Route path="/retirement" element={<RetirementCalc />} />
          <Route path="/mortgage" element={<MortgageCalc />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/seminar-ct-2026-01-28" element={<SeminarLanding />} />
          <Route path="*" element={<NotFound />} />
        </Routes>

        <Footer />
      </Router>
    </div>
  );
}
